import React, {useState, useEffect, useRef, useCallback} from 'react';
import {Link, Navigate, useLocation} from "react-router-dom";
import {useTranslation} from 'react-i18next';
import {LocalStorage} from "../../utils/LocalStorage";
import {APIClient} from "../../utils/APIClient";
import {API_URL} from "../../settings/local_settings";
import {ClientBreadcrumb} from "../client/ClientBreadcrumb";
import {useSidebar} from "../../context/SidebarContext";
import {
    User,
    Bell,
    UsersThree,
    SignOut,
    CaretDown,
    List,
} from "@phosphor-icons/react";

// Map route prefixes to breadcrumb page titles (i18n keys)
const PAGE_TITLES = {
    '/marketing': 'sidebar.tablero',
    '/agency/clients': 'sidebar.clientes',
    '/data-explorer': 'sidebar.explorador',
    '/agent/chat': 'sidebar.dashbito',
    '/reports': 'sidebar.reportes',
    '/dashbo-mcp': 'sidebar.connect',
    '/sheets-sync': 'sidebar.exportacion',
    '/agency/profile': 'sidebar.dropdown_profile',
    '/agency/conections': 'sidebar.dropdown_connections',
    '/agency-team': 'sidebar.dropdown_team',
    '/agency/alerts-configuration': 'sidebar.dropdown_alerts',
    '/agency/subscription': 'sidebar.dropdown_subscription',
};

export function TopBar() {
    const {t} = useTranslation();
    const location = useLocation();
    const {isMobile, toggleSidebar} = useSidebar();
    const [loggedOut, setLoggedOut] = useState(false);
    const [userName, setUserName] = useState('');
    const [userId, setUserId] = useState(null);
    const [clientName, setClientName] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const avatarRef = useRef(null);
    const dropdownRef = useRef(null);

    const path = location.pathname;

    // Extract client ID from path like /client/123/...
    const clientMatch = path.match(/^\/client\/(\d+)/);
    const clientId = clientMatch ? clientMatch[1] : null;

    useEffect(() => {
        const storedUserId = LocalStorage.fetchUserId();
        setUserId(storedUserId);

        new APIClient(API_URL + 'get-logged-user-data/').call('GET').then((data) => {
            if (data && data.status === 'OK') {
                setUserName(data.name || data.email || '');
            }
        });
    }, []);

    // Fetch client name when on a client page
    useEffect(() => {
        if (clientId) {
            new APIClient(API_URL + 'client/' + clientId + '/basic-data').call('GET').then((data) => {
                if (data && data.status === 'OK') {
                    setClientName(data.data?.name || '');
                }
            }).catch(() => {});
        } else {
            setClientName('');
        }
    }, [clientId]);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClick(e) {
            if (showDropdown &&
                dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                avatarRef.current && !avatarRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showDropdown]);

    const handleLogout = useCallback(() => {
        LocalStorage.removeUserData();
        setLoggedOut(true);
    }, []);

    if (loggedOut) return <Navigate to="/login"/>;

    const initials = userName
        ? userName.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
        : '?';

    const getDropdownStyle = () => {
        if (!avatarRef.current) return {};
        const rect = avatarRef.current.getBoundingClientRect();
        return {
            top: rect.bottom + 4,
            right: window.innerWidth - rect.right,
        };
    };

    // Auto-generate breadcrumb content
    const renderBreadcrumb = () => {
        // Client pages: "Clientes > ClientName"
        if (clientId) {
            return (
                <ClientBreadcrumb clientId={clientId} clientName={clientName}/>
            );
        }

        // Non-client pages: find matching page title
        for (const [prefix, titleKey] of Object.entries(PAGE_TITLES)) {
            if (path === prefix || path.startsWith(prefix + '/')) {
                return <span className="topbar-breadcrumb-item current">{t(titleKey)}</span>;
            }
        }

        return null;
    };

    return (
        <>
            <div className="topbar">
                <div className="topbar-breadcrumb">
                    {isMobile && (
                        <button className="topbar-mobile-toggle" onClick={toggleSidebar}>
                            <List size={22} weight="bold"/>
                        </button>
                    )}
                    {renderBreadcrumb()}
                </div>

                <div className="topbar-actions">
                    <button
                        ref={avatarRef}
                        className="topbar-avatar-btn"
                        onClick={() => setShowDropdown(prev => !prev)}
                    >
                        <div className="topbar-avatar">{initials}</div>
                        <span className="topbar-user-name">{userName}</span>
                        <span className="topbar-caret"><CaretDown size={14}/></span>
                    </button>
                </div>
            </div>

            <div
                ref={dropdownRef}
                className={`topbar-dropdown${showDropdown ? ' show' : ''}`}
                style={showDropdown ? getDropdownStyle() : {}}
            >
                <div className="topbar-dropdown-section-title">{t('topbar.my_account')}</div>
                {userId && (
                    <Link
                        className="topbar-dropdown-item"
                        to={`/user/${userId}/profile`}
                        onClick={() => setShowDropdown(false)}
                    >
                        <span className="icon"><User size={18}/></span>
                        {t('topbar.profile')}
                    </Link>
                )}
                {userId && (
                    <Link
                        className="topbar-dropdown-item"
                        to={`/user/${userId}`}
                        onClick={() => setShowDropdown(false)}
                    >
                        <span className="icon"><Bell size={18}/></span>
                        {t('topbar.alerts')}
                    </Link>
                )}
                <Link
                    className="topbar-dropdown-item"
                    to="/clients/"
                    onClick={() => setShowDropdown(false)}
                >
                    <span className="icon"><UsersThree size={18}/></span>
                    {t('topbar.my_clients')}
                </Link>
                <div className="topbar-dropdown-divider"/>
                <button
                    className="topbar-dropdown-item -danger"
                    onClick={handleLogout}
                    style={{width: '100%', border: 'none', background: 'none', textAlign: 'left'}}
                >
                    <span className="icon"><SignOut size={18}/></span>
                    {t('topbar.logout')}
                </button>
            </div>
        </>
    );
}
